import { useState } from 'react';
import type { CustomFilterEditorProps } from '@components/ui/FilterMenu';
import { useDebounce } from '@hooks/useDebounce';
import { Alert, Autocomplete, Box, Button, TextField } from '@mui/material';

import { useAdminClaimPatientOptionsQuery } from '../store/hooks';

type Props = CustomFilterEditorProps & {
  organizationId: string;
  searchLabel: string;
};

/** Read-only search within the target clinic, independent of the claims page. */
export function AdminClaimPatientFilter({
  organizationId,
  searchLabel,
  autoFocus,
  onChange,
  onClose,
}: Props) {
  const [search, setSearch] = useState('');
  const q = useDebounce(search.trim(), 250);
  const { currentData, error, isFetching, refetch } = useAdminClaimPatientOptionsQuery({
    organizationId,
    q,
    limit: 20,
  });

  return (
    <Box sx={{ p: 1.5 }}>
      <Autocomplete
        autoHighlight
        openOnFocus
        slotProps={{ popper: { sx: { zIndex: theme => theme.zIndex.modal + 2 } } }}
        options={search.trim() === q ? (currentData?.patients ?? []) : []}
        getOptionKey={patient => patient.patient_id}
        getOptionLabel={patient => patient.name}
        isOptionEqualToValue={(option, value) => option.patient_id === value.patient_id}
        filterOptions={options => options}
        loading={isFetching || search.trim() !== q}
        value={null}
        onInputChange={(_event, value, reason) => {
          if (reason === 'input' || reason === 'clear') setSearch(value);
        }}
        onChange={(_event, patient) => {
          if (patient) {
            onChange({ kind: 'multiSelect', values: [patient.patient_id] });
            onClose();
          }
        }}
        renderInput={params => (
          <TextField {...params} autoFocus={autoFocus} label={searchLabel} size="small" />
        )}
      />
      {error ? (
        <Alert severity="error" sx={{ mt: 1 }} action={<Button onClick={refetch}>Retry</Button>}>
          Search could not be loaded.
        </Alert>
      ) : null}
    </Box>
  );
}
